import { useState, useEffect, useMemo } from 'react';
import { getActiveProfile } from '../utils/connectorConfig';
import {
  Database,
  Shield,
  FileText,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Settings,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
  Plus,
  Plug
} from 'lucide-react';
import apiClient from '../services/api';
import axios from 'axios';
import useStore from '../store/useStore';

export default function ProviderDashboard() {
  const { setConnectorFormOpen, connectorKey } = useStore();
  const activeProfile = useMemo(() => getActiveProfile(), [connectorKey]);
  const providerUrl = activeProfile?.providerEdcUrl || '';
  const apiKey = activeProfile?.providerApiKey || '';

  const [stats, setStats] = useState({
    assets: 0,
    policies: 0,
    contractDefinitions: 0,
    activeContracts: 0
  });
  const [assets, setAssets] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [contractDefinitions, setContractDefinitions] = useState([]);
  const [apiStatus, setApiStatus] = useState({
    backend: 'checking',
    edc: 'checking'
  });
  const [loading, setLoading] = useState(true);
  const [assetsPage, setAssetsPage] = useState(1);
  const [policiesPage, setPoliciesPage] = useState(1);
  const [contractsPage, setContractsPage] = useState(1);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(null); // 'assets' | 'policies' | 'contracts' | null
  const itemsPerPage = 12;

  useEffect(() => {
    // Test backend API connectivity on mount
    testAPIConnectivity();

    if (providerUrl) {
      fetchProviderStatistics();
    } else {
      setLoading(false);
    }
  }, [providerUrl]);

  // Reset to page 1 when section changes
  useEffect(() => {
    setAssetsPage(1);
    setPoliciesPage(1);
    setContractsPage(1);
  }, [activeSection]);

  const fetchProviderStatistics = async () => {
    if (!providerUrl) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setApiStatus(prev => ({ ...prev, edc: 'checking' }));

      // Check if the providerUrl is external (non-HTTPS or different domain)
      const isExternalConnector = providerUrl.startsWith('http://') ||
                                   !providerUrl.startsWith('https://enact-dataspace.iti.gr');

      const requestBody = {
        '@context': {},
        'limit': 1000,
        'offset': 0
      };

      let assetsRes, policiesRes, contractDefsRes;

      if (isExternalConnector) {
        // Use proxy for external/HTTP connectors to avoid CORS and Mixed Content issues
        const headers = {
          'Content-Type': 'application/json'
        };
        const token = localStorage.getItem('kc_token');
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Fetch data from EDC Management API using POST requests through proxy
        [assetsRes, policiesRes, contractDefsRes] = await Promise.all([
          axios.post('/api/v1/edc-proxy', {
            targetUrl: providerUrl,
            path: '/api/management/v3/assets/request',
            method: 'POST',
            data: requestBody,
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          }, { headers }).catch(() => ({ data: [] })),
          axios.post('/api/v1/edc-proxy', {
            targetUrl: providerUrl,
            path: '/api/management/v2/policydefinitions/request',
            method: 'POST',
            data: requestBody,
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          }, { headers }).catch(() => ({ data: [] })),
          axios.post('/api/v1/edc-proxy', {
            targetUrl: providerUrl,
            path: '/api/management/v2/contractdefinitions/request',
            method: 'POST',
            data: requestBody,
            headers: apiKey ? { 'X-Api-Key': apiKey } : {}
          }, { headers }).catch(() => ({ data: [] })),
        ]);
      } else {
        // Direct request for local HTTPS connectors
        const headers = {
          'Content-Type': 'application/json',
          ...(apiKey ? { 'X-Api-Key': apiKey } : {})
        };

        // Fetch data from EDC Management API using POST requests
        [assetsRes, policiesRes, contractDefsRes] = await Promise.all([
          axios.post(`${providerUrl}/api/management/v3/assets/request`, requestBody, { headers }).catch(() => ({ data: [] })),
          axios.post(`${providerUrl}/api/management/v2/policydefinitions/request`, requestBody, { headers }).catch(() => ({ data: [] })),
          axios.post(`${providerUrl}/api/management/v2/contractdefinitions/request`, requestBody, { headers }).catch(() => ({ data: [] })),
        ]);
      }

      const assetsData = Array.isArray(assetsRes.data) ? assetsRes.data : [];
      const policiesData = Array.isArray(policiesRes.data) ? policiesRes.data : [];
      const contractDefsData = Array.isArray(contractDefsRes.data) ? contractDefsRes.data : [];

      setAssets(assetsData);
      setPolicies(policiesData);
      setContractDefinitions(contractDefsData);

      setStats({
        assets: assetsData.length,
        policies: policiesData.length,
        contractDefinitions: contractDefsData.length,
        activeContracts: 0, // Not available in management API
      });

      setApiStatus(prev => ({ ...prev, edc: 'connected' }));
    } catch (error) {
      console.error('Failed to fetch provider statistics:', error);
      setApiStatus(prev => ({ ...prev, edc: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const testAPIConnectivity = async () => {
    try {
      await apiClient.get('/data-exchange/status');
      setApiStatus(prev => ({ ...prev, backend: 'connected' }));
    } catch (error) {
      setApiStatus(prev => ({ ...prev, backend: 'error' }));
    }
  };

  const refreshData = () => {
    fetchProviderStatistics();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="text-green-400" size={20} />;
      case 'error':
        return <AlertTriangle className="text-red-400" size={20} />;
      default:
        return <Clock className="text-yellow-400 animate-pulse" size={20} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'error':
        return 'Connection Error';
      default:
        return 'Checking...';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-white mb-2">
            Provider Dashboard
          </h1>
          <p className="text-gray-400">
            Manage your data assets and offerings
          </p>
        </div>
        {!providerUrl && (
          <button
            onClick={() => setConnectorFormOpen(true)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Plus size={16} />
            Set up connector
          </button>
        )}
      </div>

      {/* Connected banner */}
      {providerUrl && (
        <div className="card bg-green-900/10 border-green-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-400 flex-shrink-0" size={22} />
            <div>
              <h3 className="font-semibold text-green-400">{activeProfile?.name} — connected</h3>
              <p className="text-sm text-gray-400">Your EDC connector is set up and active</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refreshData} disabled={loading}
              className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button onClick={() => setConnectorFormOpen(true)}
              className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <Plus size={15} />
              Add another connector
            </button>
          </div>
        </div>
      )}

      {/* First-time setup invitation */}
      {!providerUrl && (
        <div className="card bg-blue-900/10 border-blue-800 text-center py-10">
          <div className="w-16 h-16 rounded-2xl bg-blue-600/20 border border-blue-700 flex items-center justify-center mx-auto mb-4">
            <Plug className="text-blue-400" size={28} />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Connect your provider connector</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Add a name and your Provider EDC management API URL to get started.
          </p>
          <button
            onClick={() => setConnectorFormOpen(true)}
            className="btn btn-primary inline-flex items-center gap-2 mx-auto"
          >
            <Plus size={16} />
            Set up connector
          </button>
        </div>
      )}

      {/* API Status */}
      {providerUrl && (
        <div className="grid grid-cols-1 gap-6">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Provider EDC Connector</p>
                <p className="text-lg font-semibold mt-1">{getStatusText(apiStatus.edc)}</p>
              </div>
              {getStatusIcon(apiStatus.edc)}
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {providerUrl && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <button
            onClick={() => setActiveSection(activeSection === 'assets' ? null : 'assets')}
            className="card hover:border-blue-500/50 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <Database className="text-blue-400" size={24} />
              <TrendingUp className="text-green-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {loading ? '...' : stats.assets}
            </p>
            <p className="text-gray-400 text-sm">Published Assets</p>
          </button>

          <button
            onClick={() => setActiveSection(activeSection === 'policies' ? null : 'policies')}
            className="card hover:border-green-500/50 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <Shield className="text-green-400" size={24} />
              <TrendingUp className="text-green-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {loading ? '...' : stats.policies}
            </p>
            <p className="text-gray-400 text-sm">Access Policies</p>
          </button>

          <button
            onClick={() => setActiveSection(activeSection === 'contracts' ? null : 'contracts')}
            className="card hover:border-purple-500/50 transition-all cursor-pointer text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <FileText className="text-purple-400" size={24} />
              <TrendingUp className="text-green-400" size={20} />
            </div>
            <p className="text-3xl font-bold text-white mb-2">
              {loading ? '...' : stats.contractDefinitions}
            </p>
            <p className="text-gray-400 text-sm">Contract Definitions</p>
          </button>
        </div>
      )}

      {/* Assets List - Paginated with Search */}
      {providerUrl && activeSection === 'assets' && (() => {
        // Filter assets based on search query
        const filteredAssets = assets.filter(asset => {
          if (!assetSearchQuery.trim()) return true;
          const query = assetSearchQuery.toLowerCase();
          const name = (asset.properties?.['asset:prop:name'] || asset.properties?.name || asset['@id'] || '').toLowerCase();
          const description = (asset.properties?.['asset:prop:description'] || asset.properties?.description || '').toLowerCase();
          const id = (asset['@id'] || '').toLowerCase();
          return name.includes(query) || description.includes(query) || id.includes(query);
        });

        return (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Database className="text-blue-400" size={24} />
              Published Assets ({filteredAssets.length}{assetSearchQuery ? ` of ${assets.length}` : ''})
            </h2>
            <button
              onClick={() => setActiveSection(null)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-400" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={assetSearchQuery}
              onChange={(e) => {
                setAssetSearchQuery(e.target.value);
                setAssetsPage(1); // Reset to first page on search
              }}
              placeholder="Search assets by name, description, or ID..."
              className="w-full pl-10 pr-10 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {assetSearchQuery && (
              <button
                onClick={() => setAssetSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {filteredAssets.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Search className="mx-auto mb-3 opacity-50" size={48} />
              <p>No assets found matching "{assetSearchQuery}"</p>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {filteredAssets.slice((assetsPage - 1) * itemsPerPage, assetsPage * itemsPerPage).map((asset) => (
              <div key={asset['@id']} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-colors">
                <h3 className="font-semibold text-white mb-2 truncate">
                  {asset.properties?.['asset:prop:name'] || asset.properties?.name || asset['@id']}
                </h3>
                <p className="text-sm text-gray-400 mb-2 line-clamp-2">
                  {asset.properties?.['asset:prop:description'] || asset.properties?.description || 'No description'}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="px-2 py-1 bg-blue-900/30 rounded">
                    {asset.properties?.['asset:prop:contenttype'] || 'application/json'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {Math.ceil(filteredAssets.length / itemsPerPage) > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-800">
              <p className="text-sm text-gray-400">
                Page {assetsPage} of {Math.ceil(filteredAssets.length / itemsPerPage)}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setAssetsPage(p => Math.max(1, p - 1))}
                  disabled={assetsPage === 1}
                  className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setAssetsPage(p => Math.min(Math.ceil(filteredAssets.length / itemsPerPage), p + 1))}
                  disabled={assetsPage === Math.ceil(filteredAssets.length / itemsPerPage)}
                  className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
          </>
          )}
        </div>
        );
      })()}

      {/* Policies List - Paginated */}
      {providerUrl && activeSection === 'policies' && (() => {
        const totalPoliciesPages = Math.ceil(policies.length / itemsPerPage);
        const pagedPolicies = policies.slice((policiesPage - 1) * itemsPerPage, policiesPage * itemsPerPage);
        return (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Shield className="text-green-400" size={24} />
                Access Policies ({policies.length})
              </h2>
              <button
                onClick={() => setActiveSection(null)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {pagedPolicies.map((policy) => (
                <div key={policy['@id']} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-green-500/50 transition-colors">
                  <h3 className="font-semibold text-white mb-2 truncate">
                    {policy['@id']}
                  </h3>
                  <p className="text-sm text-gray-400">
                    Policy with {Array.isArray(policy.policy?.permission) ? policy.policy.permission.length : 1} permission(s)
                  </p>
                </div>
              ))}
            </div>
            {totalPoliciesPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <p className="text-sm text-gray-400">
                  Page {policiesPage} of {totalPoliciesPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPoliciesPage(p => Math.max(1, p - 1))}
                    disabled={policiesPage === 1}
                    className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPoliciesPage(p => Math.min(totalPoliciesPages, p + 1))}
                    disabled={policiesPage === totalPoliciesPages}
                    className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Contract Definitions List - Paginated */}
      {providerUrl && activeSection === 'contracts' && (() => {
        const totalContractsPages = Math.ceil(contractDefinitions.length / itemsPerPage);
        const pagedContracts = contractDefinitions.slice((contractsPage - 1) * itemsPerPage, contractsPage * itemsPerPage);
        return (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <FileText className="text-purple-400" size={24} />
                Contract Definitions ({contractDefinitions.length})
              </h2>
              <button
                onClick={() => setActiveSection(null)}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {pagedContracts.map((contract) => (
                <div key={contract['@id']} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-colors">
                  <h3 className="font-semibold text-white mb-2 truncate">
                    {contract['@id']}
                  </h3>
                  <div className="text-sm text-gray-400 space-y-1">
                    <p>Access Policy: {contract.accessPolicyId || 'N/A'}</p>
                    <p>Contract Policy: {contract.contractPolicyId || 'N/A'}</p>
                  </div>
                </div>
              ))}
            </div>
            {totalContractsPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <p className="text-sm text-gray-400">
                  Page {contractsPage} of {totalContractsPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setContractsPage(p => Math.max(1, p - 1))}
                    disabled={contractsPage === 1}
                    className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setContractsPage(p => Math.min(totalContractsPages, p + 1))}
                    disabled={contractsPage === totalContractsPages}
                    className="btn btn-secondary px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

    </div>
  );
}